/**
 * Colores y formatos de TODAS las gráficas del HUB (0.46.0). Una sola tabla para que un perfil, un canal o un resultado
 * se pinten igual en Resumen, Analítica, Marketing y el mapa.
 *
 * Validados con el verificador de paletas (claros sobre el fondo blanco, separables con daltonismo y a simple vista):
 *  - CATEGORICO: 8 tonos en orden FIJO (el 1 siempre es el mismo): se asignan por la lista completa, no por lo que se ve,
 *    así que esconder una rebanada no le cambia el color a las demás.
 *  - NEUTRO: "Sin dato", "Otros", "Sin perfil" — no es una categoría, es la falta de una.
 *  - RAMPA: un solo tono (dorado) de claro a oscuro, para lo que tiene orden (edad, rangos de dinero).
 *  - RESULTADO: llegó / canceló / no llegó (siempre con su nombre al lado: el color no es lo único que lo dice).
 */
export const CATEGORICO = ['#b8862b', '#8E1A5F', '#12936b', '#2a6fb5', '#d0643a', '#6a4fb3', '#5c8a1f', '#d06a92'];
export const NEUTRO = '#cfc9bf';
export const RAMPA = ['#ccb173', '#b7995a', '#9e8045', '#836835', '#665027', '#47381b'];
export const RESULTADO = { llego: '#12936b', cancelo: '#d9622b', noLlego: NEUTRO } as const;
/** Un perfil, el mismo color en la dona, el detalle y el mapa. */
export const COLOR_PERSONA: Record<string, string> = {
  Sandy: '#8E1A5F', Luisa: '#b8862b', Mario: '#2a6fb5', Luz: '#12936b', 'Sin datos': NEUTRO,
  SANDY: '#8E1A5F', LUISA: '#b8862b', MARIO: '#2a6fb5', LUZ: '#12936b', SIN_DATOS: NEUTRO,
};
export const ORO = '#AC996C', ORO_OSCURO = '#8b7355', ORO_CLARO = '#cbb88e';
export const EJE = { fontSize: 11, fill: '#6b6b6b' };

/** Lo que no es una categoría de verdad (va en gris y al final). */
export const esVago = (nombre: string) => /^(sin dato|sin datos|sin perfil.*|otros?|otras.*|sin especificar|sin asignar|sin paciente|\(sin .*\))$/i.test(nombre.trim());

/** El color de cada elemento de una lista, por su posición en la lista COMPLETA (lo vago, gris). */
export function coloresDe<T extends { clave?: string; nombre: string; color?: string }>(xs: T[], fijos?: Record<string, string>): Map<string, string> {
  const m = new Map<string, string>();
  let i = 0;
  for (const x of xs) {
    const k = x.clave ?? x.nombre;
    const c = x.color || fijos?.[k] || fijos?.[x.nombre] || (esVago(x.nombre) ? NEUTRO : CATEGORICO[i++ % CATEGORICO.length]);
    m.set(k, c);
  }
  return m;
}

export const dinero = (n: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);
export const dineroK = (n: number) => (Math.abs(n) >= 1000 ? '$' + Math.round(n / 1000).toLocaleString('es-MX') + 'k' : '$' + Math.round(n || 0));
/** El dinero en un eje: "$1.5k" en vez de dos "$2k" seguidos cuando las marcas van de 500 en 500. */
export const dineroEje = (n: number) => {
  const a = Math.abs(n);
  if (a >= 10000) return '$' + Math.round(n / 1000).toLocaleString('es-MX') + 'k';
  if (a >= 1000) return '$' + (Math.round(n / 100) / 10).toLocaleString('es-MX') + 'k';
  return '$' + Math.round(n || 0);
};
export const entero = (n: number) => (n || 0).toLocaleString('es-MX');
/** Un porcentaje legible: sin decimales de 10 en adelante, uno abajo de 10 ("4.5 %"), y "<1 %" para lo que casi no pesa. */
export function porcentaje(parte: number, total: number): string {
  if (!total) return '—';
  const p = (parte / total) * 100;
  if (p > 0 && p < 0.95) return '<1 %';
  return (p >= 9.95 ? Math.round(p).toString() : (Math.round(p * 10) / 10).toString()) + ' %';
}

/** El periodo dentro de una frase: "Lo cobrado en los últimos 30 días", "De quienes llegaron este mes". */
export function enPeriodo(v: string): string {
  const r = /^r:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/.exec(v || '');
  if (r) {
    const f = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
    const [a, b] = r[1] <= r[2] ? [r[1], r[2]] : [r[2], r[1]];
    return a === b ? `el ${f(a)}` : `del ${f(a)} al ${f(b)}`;
  }
  const m = /^(\d+)d$/.exec(v || '');
  if (m) return `en los últimos ${m[1]} días`;
  return ({ '': 'en todo el historial', todo: 'en todo el historial', hoy: 'hoy', mes: 'este mes', mes_pasado: 'el mes pasado', anio: 'este año' } as Record<string, string>)[v || ''] ?? `en el periodo elegido`;
}
